import axiosInstance from './axiosConfig';
import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from '../config/api';

export interface Message {
  _id: string;
  content: string;
  thread_id: string;
  from: string;
  direction: 'inbound' | 'outbound';
  message_type:
    | 'text'
    | 'image'
    | 'document'
    | 'audio'
    | 'video'
    | 'sticker'
    | 'call'
    | 'payment';
  timestamp: string;
  status?: 'sent' | 'delivered' | 'read';
  // Media fields
  media?: {
    filename: string;
    mime_type: string;
    size: number;
    blob_url?: string;
    file_url?: string;
    blob_name?: string;
    document_id?: string;
  };
  // Call / payment metadata (message_type === 'call' | 'payment')
  metadata?: Record<string, any>;
  message_id?: string;
}

export interface Thread {
  _id: string;
  thread_id: string;
  client_id: string;
  channel: 'app';
  state: 'open' | 'pending' | 'snoozed' | 'resolved' | 'closed' | 'spam';
  title?: string;
  category?: string;
  tags?: string[];
  last_message?: Message;
  /** First client message — used to resume a vehicle thread after a staff reply. */
  first_message?: Pick<Message, 'content'>;
  unread_count?: number;
}

class ChatService {
  private socket: Socket | null = null;
  private clientId: string | null = null;

  // Initialize Socket.IO connection.
  // clientId must be passed so the socket emits client:identify on connect —
  // the backend uses this to auto-join the client into community group rooms (cg:*).
  initializeSocket(token: string, clientId?: string): Socket {
    console.log('🔌 ChatService.initializeSocket - Initializing socket connection...');
    console.log('🔌 ChatService.initializeSocket - API_BASE_URL:', API_BASE_URL);

    if (clientId) this.clientId = clientId;

    if (this.socket?.connected) {
      console.log('🔌 ChatService.initializeSocket - Socket already connected:', this.socket.id);
      // If we now have a clientId but didn't before, identify now
      if (clientId) {
        this.socket.emit('client:identify', { clientId });
      }
      return this.socket;
    }

    this.socket = io(API_BASE_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => {
      console.log('✅ ChatService Socket connected:', this.socket?.id);
      // Identify so backend auto-joins community group rooms for this client
      if (this.clientId) {
        this.socket!.emit('client:identify', { clientId: this.clientId });
        console.log(`📱 ChatService - Emitted client:identify for ${this.clientId}`);
      }
    });

    this.socket.on('disconnect', () => {
      console.log('🔌 ChatService Socket disconnected');
    });

    this.socket.on('error', (error) => {
      console.error('❌ ChatService Socket error:', error);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ ChatService Socket connect_error:', error);
    });

    return this.socket;
  }

  // Expose the raw socket for use in CommunityContext listeners
  getSocket(): Socket | null {
    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.clientId = null;
  }

  // Get all threads for the current client
  async getThreads(): Promise<Thread[]> {
    console.log('🔍 ChatService.getThreads - Fetching threads from /api/app-messages/threads');
    try {
      const response = await axiosInstance.get<{ threads: Thread[] }>('/api/app-messages/threads');
      console.log('✅ ChatService.getThreads - Response:', response.data);
      console.log('📊 ChatService.getThreads - Threads array:', response.data.threads);
      return response.data.threads || [];
    } catch (error: any) {
      console.error('❌ ChatService.getThreads - Error:', error);
      console.error('❌ ChatService.getThreads - Status:', error.response?.status);
      console.error('❌ ChatService.getThreads - Response data:', error.response?.data);
      throw error;
    }
  }

  /** Mark all unread messages in a thread as read (client view). */
  async markMessagesAsRead(threadId: string): Promise<void> {
    await axiosInstance.patch('/api/app-messages/read', { thread_id: threadId });
  }

  // Get messages for a specific thread
  async getMessages(threadId: string): Promise<Message[]> {
    console.log('🔍 ChatService.getMessages - Fetching messages for thread:', threadId);
    try {
      const response = await axiosInstance.get<{ messages: Message[] }>('/api/app-messages', {
        params: { thread_id: threadId }
      });
      console.log('✅ ChatService.getMessages - Response:', response.data);
      console.log('📊 ChatService.getMessages - Message count:', response.data.messages?.length || 0);
      return response.data.messages || [];
    } catch (error: any) {
      console.error('❌ ChatService.getMessages - Error:', error);
      console.error('❌ ChatService.getMessages - Status:', error.response?.status);
      console.error('❌ ChatService.getMessages - Response data:', error.response?.data);
      throw error;
    }
  }

  /** Persist a `message_type: 'call'` row after an in-app VoIP support session (admin + thread timeline). */
  async sendCallLogMessage(
    threadId: string,
    clientMongoId: string,
    clientIdString: string,
    opts: { durationSec: number; outcome?: string }
  ): Promise<Message> {
    const outcome = opts.outcome ?? 'completed';
    const metadata = {
      kind: 'app_voip_support',
      outcome,
      duration_seconds: opts.durationSec,
    };
    const response = await axiosInstance.post<{ data: Message & { message_id?: string } }>(
      '/api/app-messages',
      {
        thread_id: threadId,
        content: '',
        from: clientMongoId,
        client_id: clientIdString,
        message_type: 'call',
        metadata,
        duration_seconds: opts.durationSec,
      }
    );
    const raw = response.data.data;
    return {
      ...raw,
      _id: raw._id || raw.message_id || `appmsg-${Date.now()}`,
    };
  }

  // Send a text message
  async sendMessage(threadId: string, content: string, clientId: string): Promise<Message> {
    console.log('📤 ChatService.sendMessage - Sending message:', { threadId, content, clientId });
    try {
      const response = await axiosInstance.post<{ data: Message }>('/api/app-messages', {
        thread_id: threadId,
        content,
        from: clientId,
        message_type: 'text',
      });
      console.log('✅ ChatService.sendMessage - Response:', response.data);
      return response.data.data;
    } catch (error: any) {
      console.error('❌ ChatService.sendMessage - Error:', error);
      console.error('❌ ChatService.sendMessage - Status:', error.response?.status);
      console.error('❌ ChatService.sendMessage - Response data:', error.response?.data);
      throw error;
    }
  }

  // Send a media message (image, document, audio, video)
  async sendMediaMessage(threadId: string, file: File, mediaType: string, clientId: string, clientIdString?: string): Promise<Message> {
    console.log('📤 ChatService.sendMediaMessage - Sending media:', { threadId, fileName: file.name, mediaType, clientId });
    try {
      const formData = new FormData();
      formData.append('media', file);
      formData.append('thread_id', threadId);
      formData.append('from', clientId);
      formData.append('message_type', mediaType);
      if (clientIdString) {
        formData.append('client_id', clientIdString);
      }

      const response = await axiosInstance.post<{ data: Message }>('/api/app-messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('✅ ChatService.sendMediaMessage - Response:', response.data);
      return response.data.data;
    } catch (error: any) {
      console.error('❌ ChatService.sendMediaMessage - Error:', error);
      console.error('❌ ChatService.sendMediaMessage - Status:', error.response?.status);
      console.error('❌ ChatService.sendMediaMessage - Response data:', error.response?.data);
      throw error;
    }
  }

  /** Create an app thread for a service topic (use forceNew when opening from a home service tile). */
  async createTopicThread(opts: {
    category: string;
    title: string;
    clientId: string;
    /** Start a fresh thread for this topic (e.g. tapping Traffic Tickets again). */
    forceNew?: boolean;
    tags?: string[];
  }): Promise<Thread> {
    const response = await axiosInstance.post<{ thread: Thread }>('/api/threads', {
      channel: 'app',
      initiator: 'client',
      entry_point_id: 'app/client/cwy-app',
      client_id: opts.clientId,
      category: opts.category,
      title: opts.title,
      force_new: opts.forceNew === true,
      tags: opts.tags,
    });
    return response.data.thread;
  }

  // Start a new chat thread (sends first message which creates the thread)
  async startNewChat(clientMongoId: string, clientIdString: string, firstMessage: string): Promise<Message> {
    console.log('🆕 ChatService.startNewChat - Starting new chat:', { clientMongoId, clientIdString, firstMessage });
    try {
      // Use the legacy format app:{clientMongoId} which triggers thread creation in backend
      const threadId = `app:${clientMongoId}`;
      
      const response = await axiosInstance.post<{ data: Message }>('/api/app-messages', {
        thread_id: threadId,
        content: firstMessage,
        from: clientMongoId,
        client_id: clientIdString,
        message_type: 'text',
      });
      console.log('✅ ChatService.startNewChat - New chat started:', response.data);
      return response.data.data;
    } catch (error: any) {
      console.error('❌ ChatService.startNewChat - Error:', error);
      console.error('❌ ChatService.startNewChat - Response:', error.response?.data);
      throw error;
    }
  }

  onSupportTyping(callback: (data: { thread_id: string }) => void): () => void {
    if (!this.socket) {
      return () => {};
    }
    const onTyping = (data: { thread_id: string }) => callback(data);
    this.socket.on('app-support:typing', onTyping);
    return () => {
      this.socket?.off('app-support:typing', onTyping);
    };
  }

  onSupportStopTyping(callback: (data: { thread_id: string }) => void): () => void {
    if (!this.socket) {
      return () => {};
    }
    const onStop = (data: { thread_id: string }) => callback(data);
    this.socket.on('app-support:stopTyping', onStop);
    return () => {
      this.socket?.off('app-support:stopTyping', onStop);
    };
  }

  // Subscribe to new messages
  onNewMessage(callback: (message: Message) => void): () => void {
    console.log('👂 ChatService.onNewMessage - Setting up listener, socket:', this.socket?.id || 'not connected');
    if (!this.socket) {
      console.warn('⚠️ ChatService.onNewMessage - No socket connection, returning empty unsubscribe');
      return () => {};
    }

    const onNew = (data: { message: Message }) => {
      console.log('📩 ChatService - Received app-message:new event:', data);
      callback(data.message);
    };
    this.socket.on('app-message:new', onNew);

    return () => {
      console.log('🔇 ChatService.onNewMessage - Unsubscribing from app-message:new');
      this.socket?.off('app-message:new', onNew);
    };
  }

  /** Subscribe to payment (and other) message metadata updates from webhooks. */
  onMessageUpdated(callback: (message: Message) => void): () => void {
    if (!this.socket) {
      return () => {};
    }

    const handler = (data: { thread_id?: string; message?: Message }) => {
      if (!data?.message) return;
      callback(data.message);
    };

    this.socket.on('app-message:updated', handler);

    return () => {
      if (this.socket) {
        this.socket.off('app-message:updated', handler);
      }
    };
  }

  // Subscribe to thread updates
  onThreadUpdate(callback: (thread: Thread) => void): () => void {
    if (!this.socket) {
      return () => {};
    }

    this.socket.on('client:threadUpdate', (data: { thread: Thread }) => {
      callback(data.thread);
    });

    return () => {
      if (this.socket) {
        this.socket.off('client:threadUpdate');
      }
    };
  }
}

export const chatService = new ChatService();
